/**
 * Musician Configuration
 * 
 * Defines musician profiles, icon sets, and custom parameter interfaces
 * for different instrument types and performers.
 */

/**
 * Musician ID to name mapping
 */
export const MUSICIAN_IDS = {
  M0002: 'Drummer',
  M0003: 'Avior Rokach',
  M0004: 'Eldar Moshe',
  M0006: 'Eldar Moshe',
  M0007: 'Kashi',
};

/**
 * Custom parameter interface types
 */
export const CUSTOM_PARAM_INTERFACES = {
  DRUMMER_KIT_PARTS: 'drummer-kit-parts',
  KEYBOARD_RANGE: 'keyboard-range',
  LEAD_ELEMENT: 'lead-element',
};

/**
 * Drummer kit parts text abbreviations
 * Used for drummer-kit-parts custom parameter interface
 */
export const DRUMMER_KIT_PARTS = {
  0: 'ANY',  // Any kit element
  1: 'OHH',  // Open Hi-Hat
  2: 'CHH',  // Closed Hi-Hat
  3: 'RID',  // Ride Cymbal
  4: 'CRS',  // Crash Cymbal
  5: 'TOM',  // Toms
  6: 'RIM',  // Rimshot
  7: 'SNR',  // Snare
};

/**
 * Icon sets for each musician
 * Each group contains icons mapped by index
 */

const DRUMMER_ICONS = {
  group1: {
    0: 'brushes',
    1: 'mallets',
    2: 'sticks',
    3: 'clap',
    4: 'shakers',
    5: 'perc-fx',
    6: 'rim-clap',
  },
  group2: {
    0: 'kick',
    1: 'snare',
    2: 'hihat',
    3: 'toms',
    4: 'cymbal',
    5: 'cymbals',
    6: 'cowbell',
    7: 'electronic',
  },
  group3: {
    0: 'vinyl',
    1: 'old-school',
    2: 'pop-rock',
    3: 'big_reverb',
    4: 'studio',
    5: 'live_room',
  },
};

const AVIOR_ICONS = {
  group1: {
    0: 'staccato',
    1: 'tenuto',
    2: 'expressive',
    3: 'growl',
  },
  group2: {
    0: 'M0003_g2_1_fingers',
    1: 'M0003_g2_2_pick',
    2: 'M0003_g2_3_slap',
    3: 'M0003_g2_4_mute',
  },
  group3: {
    0: 'M0003_g3_1_di',
    1: 'M0003_g3_2_drive',
    2: 'M0003_g3_3_octaver',
  },
};

const ELDAR_PERCUSSION_ICONS = {
  group1: {
    0: 'sticks',
    1: 'brushes',
    2: 'mallets',
    3: 'snare_damped',
    4: 'snare_modded',
    5: 'sine_3',
    6: 'sine',
  },
  group2: {
    0: 'kick',
    1: 'snare',
    2: 'snare_cross',
    3: 'snare_rim',
    4: 'hihat',
    5: 'toms',
    6: 'cymbal',
    7: 'cowbell',
  },
  group3: {
    0: 'space_1',
    1: 'space_2',
    2: 'space_3',
    3: 'space_compressed',
    4: 'space_distorted',
    5: '80s',
  },
};

const M0004_SYNTH_ICONS = {
  group1: {
    0: 'M0004_g1_1_retro',
    1: 'M0004_g1_2_modern',
    2: 'M0004_g1_3_glide',
  },
  group2: {
    0: 'M0004_g2_1_legato',
    1: 'M0004_g2_2_punchy',
    2: 'M0004_g2_3_plucked',
  },
  group3: {
    0: 'M0004_g3_1_clean',
    1: 'M0004_g3_2_drive',
    2: 'M0004_g3_3_fat',
    3: 'M0004_g3_4_wide',
  },
};

const KASHI_PIANO_ICONS = {
  group1: {
    0: 'grand_piano',
    1: 'electric_piano',
    2: 'upright_piano',
  },
  group2: {
    0: 'staccato',
    1: 'tenuto',
    2: 'sine_2',
  },
  group3: {
    0: 'space_1',
    1: 'space_2',
    2: 'space_3',
    3: 'moon',
    4: 'space_distorted',
    5: 'coffee',
  },
};

/**
 * Map musician IDs to their icon sets
 */
export const MUSICIAN_ICON_SETS = {
  M0002: DRUMMER_ICONS,
  M0003: AVIOR_ICONS,
  M0004: M0004_SYNTH_ICONS,
  M0006: ELDAR_PERCUSSION_ICONS,
  M0007: KASHI_PIANO_ICONS,
};

/**
 * Custom icon paths for special effects by musician name
 */
export const CUSTOM_EFFECT_ICONS = {
  'Idan Shneor': 'stereo_double',
  'Avior Rokach': 'horn_ensemble',
  'Eldar Moshe': 'drum_lead_element',
  'Kashi': 'synth_analog',
  'Drummer': 'drum_lead_element',
};

/**
 * Get musician configuration by ID
 * 
 * @param {string} musicianId - Musician ID (e.g., 'M0002')
 * @returns {{name: string, icons: object}} Musician name and icon set
 */
export function getMusicianConfig(musicianId) {
  const name = MUSICIAN_IDS[musicianId] || 'Drummer';
  const icons = MUSICIAN_ICON_SETS[musicianId] || DRUMMER_ICONS;
  
  return { name, icons };
}


